import React, { useEffect, useMemo, useRef, useState } from 'react';
import { PAYS_EUROPE } from './carteEurope';

/**
 * La carte de ciblage du Sourceur v2 — et ses instruments.
 *
 * Ce fichier porte l'INSTRUMENT : la carte (CarteCiblage), sa vignette
 * (CibleMini), la fiche d'un profil choisi, le badge « trouvé en
 * élargissement » et la table des libellés de canaux. Le rapport de
 * campagne (décision, compteurs, briques, cadence) vit dans
 * SourceurPilotage.
 *
 * Règles d'honnêteté tenues ici :
 *  - un candidat sans coordonnées n'a PAS de point — il est compté dans
 *    « n non localisés », on n'invente jamais une position ;
 *  - un anneau au rayon réel n'existe que si la passe fournit centre et
 *    rayon_km ; une zone sans mesure est une surface INDICATIVE, hachurée
 *    et légendée comme telle ;
 *  - la carte situe, elle ne mesure pas : ce n'est pas un SIG.
 */


const BI = ({ icon, style }) => <i className={`bi bi-${icon}`} style={style} />;
// L'instrument carte parle la langue de la maquette : DM Sans + Space Mono
// (self-hostées), tokens --su-* de ats-theme.css — constants dans les deux
// thèmes, comme un plan papier.
const suData = { fontFamily: "'Space Mono','SFMono-Regular',Consolas,monospace" };

// Une couleur par passe — les tokens de la maquette (bleu, ambre, vert,
// prune…) ; le corail est réservé à la cible active.
const COULEURS = ['var(--su-pass-1)', 'var(--su-pass-2)', 'var(--su-pass-3)', 'var(--su-pass-4)', 'var(--su-pass-5)', 'var(--su-pass-6)'];
const couleurPasse = (n) => COULEURS[(Number(n) - 1 + COULEURS.length * 4) % COULEURS.length];

const NIVEAUX = {
  ville: 'Ville', canton: 'Canton', suisse_romande: 'Suisse romande',
  suisse: 'Suisse', frontaliers: 'Frontaliers', pays: 'Pays', europe: 'Europe',
};

/* Les canaux tels que Sourceur les nomme → ce que lit un recruteur. Un nom
   de code n'arrive jamais à l'écran ; un canal inconnu s'affiche tel quel
   plutôt que d'être masqué. */
export const CANAUX = {
  linkedin: 'LinkedIn', github: 'GitHub', google: 'Web (Google)', web: 'Web (Google)',
  jobroom: 'Job-Room (ORP)', 'job-room': 'Job-Room (ORP)', orp: 'Job-Room (ORP)',
  erpia: 'Notre base', vivier: 'Notre base', interne: 'Notre base',
};
/* Un profil de NOTRE BASE : le serveur le dit (`interne`), sinon la source. */
export const estInterne = (c) => c?.interne === true || ['vivier', 'erpia', 'interne'].includes(String(c?.source || '').toLowerCase());
export const libelleCanal = (c) => CANAUX[String(c || '').toLowerCase()] || c || null;

/* ── Projection : équirectangulaire simple, dans UN repère fixe — l'Europe.
      Frontières, anneaux et profils sont projetés une seule fois dans ce
      repère ; ce que l'écran montre n'est qu'un RECTANGLE de ce repère : la
      caméra. Élargir la zone visée ne reprojette donc rien, ça RECULE la
      caméra — et ce recul se voit, c'est lui qui raconte l'élargissement.
      Suffisant pour situer : ce n'est pas un SIG. */
const REPERE = { lonMin: -6, lonMax: 19, latMin: 40, latMax: 53.5 };
const W = 720, H = 400;
const RATIO = W / H;
const proj = (lat, lng) => ({
  x: ((lng - REPERE.lonMin) / (REPERE.lonMax - REPERE.lonMin)) * W,
  y: ((REPERE.latMax - lat) / (REPERE.latMax - REPERE.latMin)) * H,
});
// km → px : 1° de latitude ≈ 111 km, mesuré sur l'axe vertical du repère.
const kmEnPx = (km) => (km / 111) * (H / (REPERE.latMax - REPERE.latMin));

// Repères d'orientation, aux vraies coordonnées (liste du handoff).
const REPERES = [
  ['Genève', 46.2044, 6.1432], ['Lausanne', 46.5197, 6.6323], ['Berne', 46.948, 7.4474],
  ['Zurich', 47.3769, 8.5417], ['Sion', 46.2331, 7.3606], ['Bâle', 47.5596, 7.5886],
  ['Lyon', 45.764, 4.8357], ['Annecy', 45.8992, 6.1294],
];

// La Suisse du fond Natural Earth — même source que le reste de l'Europe,
// même projection : silhouette, hachures « suisse » et rognage partagent
// exactement le tracé affiché.
const SUISSE = PAYS_EUROPE.find(p => p.pays === 'Switzerland').points;

// La Suisse romande, en sommets approximatifs : les élargissements v2 sont
// ADMINISTRATIFS (ville → canton → Suisse romande → Suisse) — la surface ne
// sort jamais de Suisse, contrairement au ratissage kilométrique du canal.
const SUISSE_ROMANDE = [
  [46.13, 5.97], [46.54, 6.06], [46.98, 6.43], [47.24, 6.94], [47.37, 7.05],
  [47.02, 7.35], [46.80, 7.70], [46.45, 8.00], [46.28, 8.28], [46.02, 8.10],
  [45.93, 7.87], [45.98, 7.04], [46.05, 6.78],
];
const trace = (pts) => pts.map(([lat, lng], i) => {
  const p = proj(lat, lng);
  return `${i === 0 ? 'M' : 'L'}${p.x.toFixed(1)} ${p.y.toFixed(1)}`;
}).join(' ') + ' Z';
// Le repère est fixe : les tracés du fond ne changent JAMAIS, on les calcule
// une fois pour toutes au chargement du module.
const D_SUISSE = trace(SUISSE);
const D_ROMANDE = trace(SUISSE_ROMANDE);
const D_EUROPE = PAYS_EUROPE.map(p => ({ pays: p.pays, d: trace(p.points) }));

// Surfaces INDICATIVES des niveaux restants — hachurées et légendées comme
// telles, jamais présentées comme une mesure. frontaliers/europe débordent
// de Suisse PAR DÉFINITION ; canton est rogné à la frontière suisse.
const SURFACES = {
  canton: { lat: 46.4, lng: 6.7, rx: 60, ry: 40, rognee: true },
  frontaliers: { lat: 46.4, lng: 6.6, rx: 240, ry: 150 },
  europe: { lat: 46.8, lng: 7.0, rx: 900, ry: 480 },
};

/* ── LA CAMÉRA ────────────────────────────────────────────────────────────
   Le cadre n'est pas décoratif : il vaut échelle. On montre exactement ce
   qu'il faut pour lire la zone en cours de ratissage — au lancement une
   ville, à la fin l'Europe si le moteur est allé jusque-là. Chaque
   élargissement écarte le cadre, et ce mouvement EST l'information.

   Emprise d'une passe : le disque quand le moteur donne centre + rayon
   (une mesure), sinon le découpage administratif réel du niveau. Un niveau
   inconnu ne contribue à rien — on ne cadre pas sur une zone imaginée. */
const boite = (pts) => {
  const ps = pts.map(([lat, lng]) => proj(lat, lng));
  return { x0: Math.min(...ps.map(p => p.x)), x1: Math.max(...ps.map(p => p.x)),
    y0: Math.min(...ps.map(p => p.y)), y1: Math.max(...ps.map(p => p.y)) };
};
const B_SUISSE = boite(SUISSE);
const B_ROMANDE = boite(SUISSE_ROMANDE);
const autour = (p, rx, ry = rx) => ({ x0: p.x - rx, x1: p.x + rx, y0: p.y - ry, y1: p.y + ry });

function emprisePasse(passe) {
  const z = passe?.zone;
  if (!z) return null;
  if (z.centre?.lat != null && z.centre?.lng != null && z.rayon_km != null) {
    return autour(proj(z.centre.lat, z.centre.lng), kmEnPx(z.rayon_km));
  }
  if (z.niveau === 'suisse_romande') return B_ROMANDE;
  if (z.niveau === 'suisse') return B_SUISSE;
  const s = SURFACES[z.niveau];
  return s ? autour(proj(s.lat, s.lng), kmEnPx(s.rx), kmEnPx(s.ry)) : null;
}

// Sous ~200 km de large on ne lit plus rien d'autre que la cible : c'est le
// plancher de zoom, même pour une passe « ville » de 10 km.
const CAM_MIN = kmEnPx(200);
const MARGE = 0.16;
// À la main on descend sous le plancher automatique : à 40 km de large,
// Prilly, Renens et Lausanne sont trois points, pas un.
const ZOOM_MIN = kmEnPx(40);
// Un cadre au format de la carte, rentré dans le repère — on ne montre pas
// de vide, et jamais plus serré que ZOOM_MIN ni plus large que l'Europe.
function rentrer(c) {
  const w = Math.min(Math.max(c.w, ZOOM_MIN), W), h = w / RATIO;
  return { x: Math.min(Math.max(0, c.x), W - w), y: Math.min(Math.max(0, c.y), H - h), w, h };
}
// Zoomer d'un facteur autour d'un pivot (le curseur, sinon le centre) : le
// lieu sous la souris reste sous la souris.
function zoomerCadre(c, facteur, pivot) {
  const w = Math.min(Math.max(c.w / facteur, ZOOM_MIN), W), k = w / c.w;
  const px = pivot?.x ?? c.x + c.w / 2, py = pivot?.y ?? c.y + c.h / 2;
  return rentrer({ x: px - (px - c.x) * k, y: py - (py - c.y) * k, w, h: w / RATIO });
}

/* Le cadre à viser : tout ce qui est géographiquement vrai à cet instant —
   la cible, l'emprise de chaque passe, les profils déjà placés. Rien de
   localisé du tout → le plan large, sans prétendre viser quoi que ce soit. */
function cadrer(passes, points, cible) {
  let b = null;
  const fusion = (o) => {
    if (!o) return;
    b = b ? { x0: Math.min(b.x0, o.x0), x1: Math.max(b.x1, o.x1),
      y0: Math.min(b.y0, o.y0), y1: Math.max(b.y1, o.y1) } : { ...o };
  };
  (passes || []).forEach(p => fusion(emprisePasse(p)));
  (points || []).forEach(p => fusion(autour(p, 8)));
  if (cible?.lat != null && cible?.lng != null) fusion(autour(proj(cible.lat, cible.lng), kmEnPx(35)));
  if (!b) return { x: 0, y: 0, w: W, h: H };

  let w = Math.max(CAM_MIN, (b.x1 - b.x0) * (1 + MARGE * 2));
  let h = Math.max(CAM_MIN / RATIO, (b.y1 - b.y0) * (1 + MARGE * 2));
  // Le cadre garde le format de la carte, sinon le fond se déforme.
  if (w / h > RATIO) h = w / RATIO; else w = h * RATIO;
  // Jamais plus large que le repère : au-delà, il n'y a plus de carte.
  if (w > W) { w = W; h = H; }
  const cx = (b.x0 + b.x1) / 2, cy = (b.y0 + b.y1) / 2;
  // Recentrer, puis rentrer le cadre dans le repère (on ne montre pas du vide).
  const x = Math.min(Math.max(0, cx - w / 2), W - w);
  const y = Math.min(Math.max(0, cy - h / 2), H - h);
  return { x, y, w, h };
}

const memeCadre = (a, b) => a && b && Math.abs(a.x - b.x) < 0.5 && Math.abs(a.y - b.y) < 0.5
  && Math.abs(a.w - b.w) < 0.5 && Math.abs(a.h - b.h) < 0.5;

function useMouvementReduit() {
  const [reduit, setReduit] = useState(() => (
    typeof window !== 'undefined' && window.matchMedia
      ? window.matchMedia('(prefers-reduced-motion: reduce)').matches : false));
  useEffect(() => {
    if (typeof window === 'undefined' || !window.matchMedia) return undefined;
    const mq = window.matchMedia('(prefers-reduced-motion: reduce)');
    const maj = () => setReduit(mq.matches);
    mq.addEventListener?.('change', maj);
    return () => mq.removeEventListener?.('change', maj);
  }, []);
  return reduit;
}

/* Le recul de la caméra, en 900 ms : assez lent pour qu'on VOIE la zone
   s'ouvrir, assez court pour ne pas faire attendre. Mouvement réduit
   demandé → le cadre saute, sans transition. */
const DUREE_CAM = 900;
const adoucir = (t) => 1 - Math.pow(1 - t, 3);

/* `manuel` = { vb, duree } quand l'utilisateur a pris la caméra : son cadre
   prime sur celui du moteur. Durée 0 = la molette et le glisser, qui doivent
   coller au geste ; un court glissé pour les boutons ; le recul du moteur
   garde ses 900 ms. */
function useCamera(vise, manuel) {
  const reduit = useMouvementReduit();
  const cible = manuel ? manuel.vb : vise;
  const duree = manuel ? manuel.duree : DUREE_CAM;
  const [vb, setVb] = useState(cible);
  const courant = useRef(cible);
  const raf = useRef(0);
  useEffect(() => {
    if (memeCadre(courant.current, cible)) return undefined;
    if (reduit || duree <= 0) { courant.current = cible; setVb(cible); return undefined; }
    const de = courant.current, t0 = performance.now();
    const pas = (t) => {
      const k = Math.min(1, (t - t0) / duree), e = adoucir(k);
      const v = { x: de.x + (cible.x - de.x) * e, y: de.y + (cible.y - de.y) * e,
        w: de.w + (cible.w - de.w) * e, h: de.h + (cible.h - de.h) * e };
      courant.current = v;
      setVb(v);
      if (k < 1) raf.current = requestAnimationFrame(pas);
    };
    raf.current = requestAnimationFrame(pas);
    return () => cancelAnimationFrame(raf.current);
  }, [cible, duree, reduit]);
  return vb;
}

function Halo({ passe, ech = 1, mini, idPrefixe }) {
  const c = couleurPasse(passe.n);
  const z = passe.zone || {};
  const ep = (mini ? 2 : 1.5) * ech;
  // Rayon RÉEL fourni par le moteur : c'est la mesure du ratissage du canal
  // — elle PEUT déborder la frontière (bassin franco-genevois), c'est réel.
  if (z.centre?.lat != null && z.centre?.lng != null && z.rayon_km != null) {
    const p = proj(z.centre.lat, z.centre.lng);
    const r = Math.max((mini ? 5 : 8) * ech, kmEnPx(z.rayon_km));
    // Sur la grande carte, l'anneau se dessine à l'arrivée (pathLength 100
    // = dash normalisé, quelle que soit la circonférence réelle).
    return <circle className={mini ? undefined : 'sv2c-halo'} pathLength={mini ? undefined : 100}
      style={mini ? undefined : { animationDelay: `${(Number(passe.n) || 1) * 140}ms` }}
      cx={p.x} cy={p.y} r={r} fill={c} fillOpacity="0.07" stroke={c} strokeOpacity="0.75" strokeWidth={ep} />;
  }
  // Zones ADMINISTRATIVES : le levier v2 ne sort jamais de Suisse — on
  // hachure le découpage, pas un cercle qui mordrait sur la France.
  const hachures = { fill: `url(#${idPrefixe}-hachures-${passe.n})`, stroke: c, strokeOpacity: 0.7, strokeWidth: ep, strokeDasharray: `${7 * ech} ${5 * ech}` };
  if (z.niveau === 'suisse_romande') return <path d={D_ROMANDE} {...hachures} strokeLinejoin="round" />;
  if (z.niveau === 'suisse') return <path d={D_SUISSE} {...hachures} strokeLinejoin="round" />;
  const s = SURFACES[z.niveau];
  if (!s) return null; // niveau inconnu et sans centre : rien plutôt qu'un faux halo
  const p = proj(s.lat, s.lng);
  return (
    <ellipse cx={p.x} cy={p.y} rx={kmEnPx(s.rx)} ry={kmEnPx(s.ry)}
      {...hachures} clipPath={s.rognee ? `url(#${idPrefixe}-clip-ch)` : undefined} />
  );
}

/* La cible : le centre de la visée initiale, façon maquette — point corail
   + anneau, qui respire tant que le moteur cherche. */
function Cible({ centre, ech = 1, enCours, mini }) {
  if (!centre) return null;
  const p = proj(centre.lat, centre.lng);
  // La cible garde sa taille À L'ÉCRAN quel que soit le zoom : c'est un
  // réticule, pas une surface — elle ne mesure rien.
  const k = (mini ? 1.6 : 1) * ech;
  return (
    <g>
      <circle className={enCours ? 'sv2-cible-anneau' : undefined} cx={p.x} cy={p.y} r={11 * k} fill="none" stroke="var(--su-coral)" strokeWidth={1.6 * k} />
      <circle className={enCours ? 'sv2-cible-coeur' : undefined} cx={p.x} cy={p.y} r={4 * k} fill="var(--su-coral)" />
      <line x1={p.x - 17 * k} x2={p.x - 13 * k} y1={p.y} y2={p.y} stroke="var(--su-coral)" strokeWidth={1.4 * k} />
      <line x1={p.x + 13 * k} x2={p.x + 17 * k} y1={p.y} y2={p.y} stroke="var(--su-coral)" strokeWidth={1.4 * k} />
      <line y1={p.y - 17 * k} y2={p.y - 13 * k} x1={p.x} x2={p.x} stroke="var(--su-coral)" strokeWidth={1.4 * k} />
      <line y1={p.y + 13 * k} y2={p.y + 17 * k} x1={p.x} x2={p.x} stroke="var(--su-coral)" strokeWidth={1.4 * k} />
    </g>
  );
}

/* Le fond commun : grille, silhouette CH, hachures — partagé grand/mini.
   Les tracés sont constants (repère fixe) ; seules les épaisseurs et la
   maille de la grille suivent le zoom, pour garder la même densité à
   l'écran quand la caméra s'approche. */
function FondCarte({ passes, idPrefixe, mini, ech = 1 }) {
  const maille = 26 * ech;
  return (
    <>
      <defs>
        <pattern id={`${idPrefixe}-grille`} width={maille} height={maille} patternUnits="userSpaceOnUse">
          <path d={`M${maille} 0H0V${maille}`} fill="none" stroke="var(--su-map-grid)" strokeWidth={0.7 * ech} />
        </pattern>
        <clipPath id={`${idPrefixe}-clip-ch`}><path d={D_SUISSE} /></clipPath>
        {(passes || []).map(p => (
          <pattern key={p.n} id={`${idPrefixe}-hachures-${p.n}`} width={9 * ech} height={9 * ech} patternUnits="userSpaceOnUse" patternTransform="rotate(25)">
            <path d={`M0 0V${9 * ech}`} stroke={couleurPasse(p.n)} strokeWidth={1.1 * ech} opacity="0.35" />
          </pattern>
        ))}
      </defs>
      <rect width={W} height={H} fill="var(--su-map-bg)" />
      <rect width={W} height={H} fill={`url(#${idPrefixe}-grille)`} opacity="0.55" />
      {/* L'Europe du fond livré avec les maquettes — la mer est le fond,
          chaque pays est projeté avec la même équation que les données. */}
      {D_EUROPE.map((p, i) => (
        <path key={`${p.pays}-${i}`} d={p.d} fill="var(--su-map-terre)"
          stroke="var(--su-map-trait)" strokeWidth={(mini ? 1.1 : 0.7) * ech} strokeLinejoin="round" />
      ))}
      <path d={D_SUISSE} fill="var(--su-map-terre)" stroke="var(--su-map-trait)" strokeWidth={(mini ? 2 : 1.4) * ech} strokeLinejoin="round" />
      {!mini && (
        <text {...(() => { const p = proj(46.75, 8.1); return { x: p.x, y: p.y }; })()}
          fontSize={11 * ech} letterSpacing={2 * ech} fill="var(--su-map-ville)" style={suData}>SUISSE</text>
      )}
    </>
  );
}

// Journal : d'où viennent les passes affichées — les trois codes du contrat
// (selection / dashboard / aucune), tout autre texte est affiché tel quel.
// `note` n'existe qu'avec "aucune" et dit toujours pourquoi ; en
// selection/dashboard, passes[] est non vide.
/* Position d'un candidat : `localisation` (vue besoin) ou `geo` (dashboard),
   même objet {label, lat, lng, approx?}. null → compté « non localisé ».
   approx:"chef-lieu" → point estompé : la position est ramenée au chef-lieu,
   on le montre sans le cacher. */
const lireGeo = (c) => c?.localisation ?? c?.geo ?? null;
const estApprox = (g) => String(g?.approx || '').toLowerCase() === 'chef-lieu';

const centreDeVisee = (passes) => {
  for (const p of passes || []) if (p?.zone?.centre?.lat != null && p?.zone?.centre?.lng != null) return p.zone.centre;
  return null;
};

/** Vignette « cible et points » (~handoff §vignette) : pour les cartes du
 *  tableau de suivi. Aucune étiquette — juste la visée et les trouvailles. */
export function CibleMini({ v2, candidates, width = 180, height = 104 }) {
  if (!v2 || !Array.isArray(v2.passes) || v2.passes.length === 0) return null;
  const centre = centreDeVisee(v2.passes);
  const localises = (Array.isArray(candidates) ? candidates : []).filter(c => { const g = lireGeo(c); return g?.lat != null && g?.lng != null; });
  if (!centre && localises.length === 0) return null; // rien de géographique : pas de fausse carte
  const pts = localises.map(c => { const g = lireGeo(c); return proj(g.lat, g.lng); });
  // Même cadrage que la grande carte, figé : la vignette et le suivi
  // montrent la même chose, à deux échelles.
  const vb = cadrer(v2.passes, pts, centre);
  const ech = vb.w / W;
  const id = `sv2m-${Math.round(width)}`;
  return (
    <svg viewBox={`${vb.x} ${vb.y} ${vb.w} ${vb.h}`} preserveAspectRatio="xMidYMid slice" aria-label="Cible géographique de la campagne"
      style={{ width, height, display: 'block', borderRadius: '6px', border: '1px solid var(--su-map-line)', flex: 'none' }}>
      <FondCarte passes={v2.passes} idPrefixe={id} mini ech={ech} />
      {v2.passes.map(p => <Halo key={p.n} passe={p} mini ech={ech} idPrefixe={id} />)}
      <Cible centre={centre} ech={ech} enCours={false} mini />
      {localises.map((c, i) => (
        <circle key={c.recruiterId ?? i} cx={pts[i].x} cy={pts[i].y} r={7 * ech} fill={couleurPasse(c.passe_origine ?? 1)}
          fillOpacity={estApprox(lireGeo(c)) ? 0.5 : 1} stroke="var(--su-map-panel)" strokeWidth={2.5 * ech} />
      ))}
    </svg>
  );
}

/** La carte de ciblage — panneau autonome (en-tête, SVG, légende, fiche du
 *  profil choisi), utilisé par le suivi de campagne ET par l'écran God's eye.
 *
 *  La caméra suit le moteur : au lancement elle est serrée sur la zone
 *  visée, et chaque élargissement la fait reculer. Le mouvement n'est pas un
 *  effet — c'est la seule façon de voir que le moteur a renoncé à la
 *  proximité. Un profil trouvé tombe à sa vraie position ; un clic ouvre sa
 *  fiche avec les gestes réellement disponibles à cet instant : pendant la
 *  chasse le serveur ne sait pas encore écrire (la sélection n'est pas
 *  arrivée), la fiche le DIT au lieu d'offrir un bouton qui échouerait.
 */
const cleDe = (c, i) => String(c?.recruiterId ?? `${c?.firstName || ''}|${c?.name || ''}|${i}`);
// Le nom SANS doublon : le moteur renvoie `name` déjà complet (« Javier
// Sánchez ») ET `firstName` (« Javier ») — les coller donnait « Javier Javier
// Sánchez ». Même règle que `joinName` de l'écran Sourcing, partagée ici.
export const nomDe = (c) => {
  const f = String(c?.firstName || '').trim(), n = String(c?.name || '').trim();
  if (!f) return n || c?.nom || c?.fullName || 'Profil';
  if (!n) return f;
  return n.toLowerCase().startsWith(f.toLowerCase()) ? n : `${f} ${n}`;
};

export function lienProfil(c, src) {
  const direct = c?.publicUrl || c?.link || c?.profile_url || c?.profileUrl || c?.url || null;
  if (direct) return direct;
  const rid = String(c?.recruiterId || '').trim();
  const lk = src ? src === 'linkedin' : (rid && !rid.includes(':'));
  return lk && rid && !rid.includes(':') ? `https://www.linkedin.com/talent/profile/${rid}` : null;
}

// Où mène le lien : un profil trouvé par la recherche web (google) est une
// page LinkedIn publique ; GitHub et Job-Room ont leur propre page.
export const ICONE_SOURCE = { linkedin: 'linkedin', google: 'linkedin', github: 'github', jobroom: 'briefcase', vivier: 'people' };
export const versLinkedin = (c) => ['linkedin', 'google'].includes(String(c?.source || 'linkedin').toLowerCase());
export const libelleSource = (c) => {
  const s = String(c?.source || 'linkedin').toLowerCase();
  if (s === 'google') return 'LinkedIn (page publique)';
  return libelleCanal(s) || 'LinkedIn';
};

// « Profil LinkedIn » / « Profil GitHub » : le lien texte des fiches et de la
// sélection. Repli sur l'URL Recruiter déduite de l'identifiant (anciennes
// sélections sans lien) — la même règle que le moteur. Rien si aucun lien.
export function LienProfilSource({ c, style }) {
  const lien = lienProfil(c);
  if (!lien) return null;
  const li = versLinkedin(c);
  const s = String(c?.source || 'linkedin').toLowerCase();
  const titre = li
    ? (c?.publicUrl || s === 'google' ? 'Profil LinkedIn public' : 'Profil LinkedIn Recruiter (nécessite un accès Recruiter)')
    : `Ouvrir le profil sur ${libelleCanal(s) || 'sa source'}`;
  return (
    <a href={lien} target="_blank" rel="noreferrer" onClick={e => e.stopPropagation()} title={titre}
      style={{ display: 'inline-flex', alignItems: 'center', gap: '6px', fontSize: '12px', fontWeight: 600, color: li ? 'var(--erp-linkedin)' : 'var(--erp-ink-strong)', textDecoration: 'none', ...(style || {}) }}>
      <BI icon={ICONE_SOURCE[s] || 'box-arrow-up-right'} style={{ fontSize: '13px' }} />{li ? 'Profil LinkedIn' : `Profil ${libelleCanal(s) || 'source'}`}
    </a>
  );
}

export function CarteCiblage({ v2, candidates, cible, actions, cibleProfil }) {
  const [legendeCachee, setLegendeCachee] = useState(false);
  const [choisie, setChoisie] = useState(null); // clé du groupe ouvert
  const [rang, setRang] = useState(0);          // profil montré dans ce groupe
  // La main sur la caméra (molette, boutons, glisser) : { vb, duree } ou
  // null quand c'est le moteur qui cadre.
  const [manuel, setManuel] = useState(null);
  const [glissant, setGlissant] = useState(false);
  // Échap referme la fiche : elle se pose par-dessus la carte, on doit
  // pouvoir en sortir sans viser la croix.
  useEffect(() => {
    if (!choisie) return undefined;
    const surTouche = (e) => { if (e.key === 'Escape') setChoisie(null); };
    window.addEventListener('keydown', surTouche);
    return () => window.removeEventListener('keydown', surTouche);
  }, [choisie]);

  const passes = useMemo(() => (Array.isArray(v2?.passes) ? v2.passes : []), [v2]);
  const cands = useMemo(() => (Array.isArray(candidates) ? candidates : []), [candidates]);
  const localises = useMemo(() => cands.filter(c => { const g = lireGeo(c); return g?.lat != null && g?.lng != null; }), [cands]);
  const points = useMemo(() => localises.map(c => { const g = lireGeo(c); return proj(g.lat, g.lng); }), [localises]);
  // La visée : le centre de la première passe mesurée, sinon la ville saisie
  // au lancement (le serveur la rend tant qu'aucune passe n'est publiée).
  const centre = useMemo(() => centreDeVisee(passes)
    || (cible?.lat != null && cible?.lng != null ? cible : null), [passes, cible]);

  // La caméra ne bouge que quand la GÉOGRAPHIE bouge : elle se recalcule sur
  // une signature des zones et des positions, pas à chaque rendu (sinon le
  // polling la ferait trembler toutes les trois secondes).
  const signature = useMemo(() => [
    passes.map(p => `${p.n}/${p.zone?.niveau || ''}/${p.zone?.rayon_km ?? ''}/${p.zone?.centre?.lat ?? ''},${p.zone?.centre?.lng ?? ''}`).join('|'),
    points.map(p => `${p.x.toFixed(0)},${p.y.toFixed(0)}`).join(';'),
    centre ? `${centre.lat},${centre.lng}` : '',
  ].join('#'), [passes, points, centre]);
  // eslint-disable-next-line react-hooks/exhaustive-deps -- la signature EST la dépendance utile : passes/points sont des tableaux recréés à chaque rendu.
  const vise = useMemo(() => cadrer(passes, points, centre), [signature]);
  const vb = useCamera(vise, manuel);
  const ech = vb.w / W;
  const vbRef = useRef(vb); vbRef.current = vb;
  const svgRef = useRef(null);
  // La main prime sur le moteur jusqu'au recadrage — ou jusqu'à la passe
  // suivante : le recul de la caméra EST l'information, on ne le rate pas
  // parce qu'on regardait un quartier. Un profil de plus ne reprend pas la
  // caméra (pendant la chasse il en tombe un toutes les quelques secondes).
  const clePasses = passes.map(p => `${p.n}/${p.zone?.niveau || ''}/${p.zone?.rayon_km ?? ''}`).join('|');
  useEffect(() => { setManuel(null); }, [clePasses]);
  // Le lieu de la carte sous le curseur — pivot du zoom à la molette.
  const sousCurseur = (e) => {
    const r = svgRef.current?.getBoundingClientRect(), c = vbRef.current;
    if (!r || !r.width || !r.height) return null;
    return { x: c.x + ((e.clientX - r.left) / r.width) * c.w, y: c.y + ((e.clientY - r.top) / r.height) * c.h };
  };
  const zoomer = (facteur, pivot = null, duree = 220) => setManuel({ vb: zoomerCadre(vbRef.current, facteur, pivot), duree });
  const recadrer = () => setManuel(null);
  // La molette zoome sur la carte, comme sur n'importe quel plan. React
  // écoute `wheel` en passif : on s'abonne à la main pour pouvoir retenir
  // le défilement de la page pendant le geste.
  useEffect(() => {
    const el = svgRef.current;
    if (!el) return undefined;
    const surMolette = (e) => {
      e.preventDefault();
      const f = Math.exp(-Math.max(-60, Math.min(60, e.deltaY)) * 0.004);
      zoomer(f, sousCurseur(e), 0);
    };
    el.addEventListener('wheel', surMolette, { passive: false });
    return () => el.removeEventListener('wheel', surMolette);
    // eslint-disable-next-line react-hooks/exhaustive-deps -- zoomer/sousCurseur ne lisent que des refs.
  }, []);
  // Glisser pour déplacer. Un clic reste un clic : on ne bascule en
  // déplacement qu'après 4 px, et le clic qui suit un glissé est avalé pour
  // ne pas ouvrir la fiche du point sur lequel on a lâché.
  const glisse = useRef(null);
  const aGlisse = useRef(false);
  const surPointerDown = (e) => {
    if (e.button !== 0) return;
    aGlisse.current = false;
    glisse.current = { x: e.clientX, y: e.clientY, cadre: vbRef.current, bouge: false, id: e.pointerId };
  };
  const surPointerMove = (e) => {
    const g = glisse.current;
    if (!g) return;
    if (!g.bouge) {
      if (Math.hypot(e.clientX - g.x, e.clientY - g.y) < 4) return;
      g.bouge = true; setGlissant(true);
      try { svgRef.current?.setPointerCapture(g.id); } catch (_) { /* pointeur déjà parti */ }
    }
    const r = svgRef.current?.getBoundingClientRect();
    if (!r || !r.width || !r.height) return;
    const dx = ((e.clientX - g.x) / r.width) * g.cadre.w, dy = ((e.clientY - g.y) / r.height) * g.cadre.h;
    setManuel({ vb: rentrer({ ...g.cadre, x: g.cadre.x - dx, y: g.cadre.y - dy }), duree: 0 });
  };
  const surPointerUp = () => {
    const g = glisse.current;
    glisse.current = null;
    if (g?.bouge) { aGlisse.current = true; setGlissant(false); }
  };
  const avalerClicApresGlisse = (e) => {
    if (!aGlisse.current) return;
    aGlisse.current = false;
    e.stopPropagation(); e.preventDefault();
  };
  const surTouche = (e) => {
    if (e.key === '+' || e.key === '=') { e.preventDefault(); zoomer(1.6); }
    else if (e.key === '-') { e.preventDefault(); zoomer(1 / 1.6); }
    else if (e.key === '0') { e.preventDefault(); recadrer(); }
  };

  // Deux profils distants de 25 km sont, à l'échelle de l'Europe, le MÊME
  // pixel : le point du dessous devient alors impossible à cliquer. On les
  // rassemble donc en un point chiffré — regroupement de RENDU, décidé à
  // l'échelle visée (pas à chaque image de l'animation, sinon les points
  // sauteraient). Aucune position n'est perdue : la fiche montre le lieu
  // réel de chaque profil du groupe.
  const echCible = (manuel ? manuel.vb : vise).w / W;
  const groupes = useMemo(() => {
    const seuil = 13 * echCible;
    const amas = [];
    localises.forEach(c => {
      const g = lireGeo(c);
      const p = proj(g.lat, g.lng);
      const proche = amas.find(a => Math.hypot(a.p.x - p.x, a.p.y - p.y) <= seuil);
      if (proche) {
        proche.membres.push(c); proche.pts.push(p);
        proche.p = { x: proche.pts.reduce((s2, q) => s2 + q.x, 0) / proche.pts.length,
          y: proche.pts.reduce((s2, q) => s2 + q.y, 0) / proche.pts.length };
      } else amas.push({ p, pts: [p], membres: [c] });
    });
    return amas.map(a => {
      const lieux = [...new Set(a.membres.map(c => lireGeo(c)?.label).filter(Boolean))];
      return { ...a,
        // La clé tient au pixel : un profil qui rejoint le groupe ne referme
        // pas la fiche ouverte.
        cle: `${a.p.x.toFixed(0)},${a.p.y.toFixed(0)}`,
        label: lieux.length === 1 ? lieux[0] : lieux.slice(0, 2).join(' · ') + (lieux.length > 2 ? '…' : ''),
        approx: a.membres.some(c => estApprox(lireGeo(c))) };
    });
  }, [localises, echCible]);

  // Viser UN profil depuis la liste des retenus : la fiche de son groupe
  // s'ouvre sur lui et la caméra vient dessus — une quarantaine de km de
  // large, jamais plus serré que le cadre courant s'il l'est déjà. Le jeton
  // `tick` change à chaque clic : re-cliquer la même ligne re-cadre, et un
  // zoom à la molette ne ramène pas la caméra sur le profil.
  const groupesRef = useRef(groupes); groupesRef.current = groupes;
  useEffect(() => {
    const rid = cibleProfil && cibleProfil.recruiterId != null ? String(cibleProfil.recruiterId) : '';
    if (!rid) return;
    const g = groupesRef.current.find(x => x.membres.some(m => String(m.recruiterId) === rid));
    if (!g) return;
    setChoisie(g.cle);
    setRang(Math.max(0, g.membres.findIndex(m => String(m.recruiterId) === rid)));
    const c0 = vbRef.current;
    const w = Math.min(c0.w, kmEnPx(40));
    const h = w * (c0.h / c0.w);
    setManuel({ vb: rentrer({ x: g.p.x - w / 2, y: g.p.y - h / 2, w, h }), duree: 320 });
  // eslint-disable-next-line react-hooks/exhaustive-deps -- le jeton du clic est la seule dépendance voulue.
  }, [cibleProfil?.tick]);

  if (!v2) return null;
  const nonLocalises = cands.length - localises.length;
  const passeActive = passes[passes.length - 1];
  const fraicheur = passes.map(p => p.le).filter(Boolean).map(d => new Date(d)).filter(d => !Number.isNaN(d.getTime())).sort((a, b) => b - a)[0] || null;
  const enCours = v2.statut === 'en_cours';
  const pCible = centre ? proj(centre.lat, centre.lng) : null;
  // Rien de géographique et rien en cours : pas de carte vide décorative.
  if (passes.length === 0 && !centre && points.length === 0 && !enCours) return null;
  const largeurKm = Math.round(vb.w / kmEnPx(1));
  const groupeOuvert = groupes.find(g => g.cle === choisie) || null;
  const ouvrir = (cle) => { setChoisie(prev => (prev === cle ? null : cle)); setRang(0); };

  return (
    <div className="sv2-no-print" style={{ background: 'var(--su-map-panel)', border: '1px solid var(--su-map-line)', borderRadius: '12px', overflow: 'hidden' }}>
      {/* La vie de la carte : balayage radar tant que le moteur cherche,
          points qui tombent en cascade, anneaux qui se dessinent — le tout
          coupé si l'utilisateur préfère réduire les animations. */}
      <style>{`
        @keyframes sv2c-pop { from { opacity: 0; transform: scale(0); } 60% { opacity: 1; transform: scale(1.35); } to { opacity: 1; transform: scale(1); } }
        @keyframes sv2c-ping { from { opacity: .65; transform: scale(1); } to { opacity: 0; transform: scale(2.4); } }
        @keyframes sv2c-tour { to { transform: rotate(360deg); } }
        @keyframes sv2c-trace { from { stroke-dashoffset: 100; } to { stroke-dashoffset: 0; } }
        .sv2c-pt { animation: sv2c-pop 480ms cubic-bezier(.22,.8,.25,1) both; transform-box: fill-box; transform-origin: center; transition: transform 120ms ease; }
        .sv2c-clic { cursor: pointer; outline: none; }
        .sv2c-clic:hover .sv2c-pt { transform: scale(1.45); }
        .sv2c-focus { opacity: 0; }
        .sv2c-clic:focus-visible .sv2c-focus { opacity: 1; }
        .sv2c-ping { animation: sv2c-ping 2.4s ease-out infinite; transform-box: fill-box; transform-origin: center; }
        .sv2c-sweep { animation: sv2c-tour 5.5s linear infinite; }
        .sv2c-halo { stroke-dasharray: 100; animation: sv2c-trace 900ms ease-out both; }
        @media (prefers-reduced-motion: reduce) {
          .sv2c-pt, .sv2c-ping, .sv2c-sweep, .sv2c-halo { animation: none; }
          .sv2c-clic:hover .sv2c-pt { transform: none; }
          .sv2c-ping { opacity: 0; }
          .sv2c-halo { stroke-dasharray: none; }
        }
        /* La caméra à la main : trois boutons, mêmes jetons que la légende. */
        .sv2c-cam { position: absolute; top: 12px; left: 13px; display: flex; flex-direction: column; gap: 4px; }
        .sv2c-cam__btn { width: 28px; height: 28px; display: flex; align-items: center; justify-content: center; padding: 0;
          border: 1px solid var(--su-map-line); background: var(--su-map-panel); color: var(--su-map-sub); border-radius: 5px;
          cursor: pointer; font-size: 12px; transition: color 120ms ease, border-color 120ms ease; }
        .sv2c-cam__btn:hover:not(:disabled) { color: var(--su-map-ink); border-color: var(--su-map-sub); }
        .sv2c-cam__btn:focus-visible { outline: 2px solid var(--su-coral); outline-offset: 1px; }
        .sv2c-cam__btn:disabled { opacity: .38; cursor: default; }
        .sv2c-cam__btn--recadrer { color: var(--su-coral); border-color: var(--su-coral); }
        .sv2c-cam__btn--recadrer:hover:not(:disabled) { color: var(--su-coral); border-color: var(--su-coral); background: var(--su-map-veil); }
        @media (prefers-reduced-motion: reduce) { .sv2c-cam__btn { transition: none; } }
        /* Les gestes de la fiche vivent dans l'espace de jetons de la carte
           (--su-map-*) : l'instrument garde le même rendu dans les deux
           thèmes, comme un plan papier. */
        .sv2f-btn { display: flex; align-items: center; justify-content: center; gap: 6px;
          width: 100%; padding: 7px 10px; border-radius: 7px; border: 1px solid var(--su-map-line);
          background: transparent; color: var(--su-map-ink);
          font-family: 'DM Sans','IBM Plex Sans',sans-serif; font-size: 11.5px; font-weight: 600;
          cursor: pointer; transition: border-color 120ms ease, color 120ms ease; }
        .sv2f-btn:hover:not(:disabled) { border-color: var(--su-map-ink); }
        .sv2f-btn:focus-visible { outline: 2px solid var(--su-coral); outline-offset: 1px; }
        .sv2f-btn:disabled { cursor: wait; opacity: .6; }
        .sv2f-btn--cta { border-color: var(--su-coral); color: var(--su-coral); }
        .sv2f-btn--lien { text-decoration: none; margin-top: 8px; }
        .sv2f-btn--court { width: auto; margin-top: 0; padding: 5px 9px; font-size: 11px; }
        .sv2f-btn--cta:hover:not(:disabled) { border-color: var(--su-coral); }
        .sv2f-fait { cursor: default; color: var(--ok); }
        .sv2f-fermer { position: absolute; top: 8px; right: 8px; width: 22px; height: 22px;
          display: flex; align-items: center; justify-content: center; padding: 0;
          border: 1px solid var(--su-map-line); background: var(--su-map-panel);
          color: var(--su-map-sub); cursor: pointer; border-radius: 5px; }
        .sv2f-fermer:hover { color: var(--su-map-ink); }
        .sv2f-fermer:focus-visible { outline: 2px solid var(--su-coral); outline-offset: 1px; }
        .sv2f-onglet { padding: 3px 8px; border-radius: 5px; border: 1px solid var(--su-map-line);
          background: transparent; color: var(--su-map-sub);
          font-family: 'DM Sans','IBM Plex Sans',sans-serif; font-size: 10.5px; font-weight: 600;
          cursor: pointer; max-width: 100%; overflow: hidden; text-overflow: ellipsis; white-space: nowrap; }
        .sv2f-onglet:hover { color: var(--su-map-ink); }
        .sv2f-onglet:focus-visible { outline: 2px solid var(--su-coral); outline-offset: 1px; }
        .sv2f-onglet--on { border-color: var(--su-coral); color: var(--su-coral); }
        @media (prefers-reduced-motion: reduce) { .sv2f-btn { transition: none; } }
      `}</style>
      <div style={{ display: 'flex', alignItems: 'center', gap: '10px', flexWrap: 'wrap', padding: '11px 16px', borderBottom: '1px solid var(--su-map-line)' }}>
        <BI icon="geo-alt" style={{ color: 'var(--su-coral)', fontSize: '16px' }} />
        <span style={{ fontSize: '12px', fontWeight: 700, color: 'var(--su-map-ink)' }}>Cible géographique</span>
        {passeActive?.zone && (
          <span style={{ ...suData, fontSize: '10px', letterSpacing: '0.08em', textTransform: 'uppercase', color: 'var(--su-map-sub)' }}>
            Passe {String(passeActive.n).padStart(2, '0')} · {passeActive.zone.label || NIVEAUX[passeActive.zone.niveau] || passeActive.zone.niveau}
          </span>
        )}
        {passes.length === 0 && enCours && (
          <span style={{ ...suData, fontSize: '10px', letterSpacing: '0.08em', textTransform: 'uppercase', color: 'var(--su-map-sub)' }}>
            <BI icon="arrow-repeat" style={{ display: 'inline-block', animation: 'sv2-spin .9s linear infinite', marginRight: '5px' }} />
            Première passe en préparation
          </span>
        )}
        <span style={{ flex: 1 }} />
        {/* Le cadre vaut échelle : on dit sa largeur, sinon un dézoom ne se
            distingue pas d'un changement de dessin. */}
        <span title={manuel ? 'Largeur de la zone montrée à l\'écran — cadrée à la main. Recadrer (0) rend la caméra au moteur.' : 'Largeur de la zone montrée à l\'écran — elle s\'agrandit à chaque élargissement du moteur.'}
          style={{ ...suData, fontSize: '9.5px', letterSpacing: '0.08em', textTransform: 'uppercase', color: 'var(--su-map-label)' }}>
          Cadre · {largeurKm.toLocaleString('fr-CH')} km{manuel ? ' · à la main' : ''}
        </span>
        {fraicheur && (
          <span style={{ ...suData, fontSize: '9.5px', letterSpacing: '0.08em', textTransform: 'uppercase', color: 'var(--su-map-label)' }}>
            Fraîcheur · {fraicheur.toLocaleDateString('fr-CH', { day: '2-digit', month: 'short' })} {fraicheur.toLocaleTimeString('fr-CH', { hour: '2-digit', minute: '2-digit' })}
          </span>
        )}
      </div>
      <div style={{ position: 'relative' }} onClickCapture={avalerClicApresGlisse} onKeyDown={surTouche}>
        <svg ref={svgRef} viewBox={`${vb.x.toFixed(2)} ${vb.y.toFixed(2)} ${vb.w.toFixed(2)} ${vb.h.toFixed(2)}`}
          style={{ width: '100%', height: 'auto', display: 'block', cursor: glissant ? 'grabbing' : 'grab', touchAction: 'none' }}
          aria-label="Carte de ciblage — zone visée et profils trouvés. Molette ou boutons pour zoomer, glisser pour déplacer."
          onPointerDown={surPointerDown} onPointerMove={surPointerMove} onPointerUp={surPointerUp} onPointerCancel={surPointerUp}>
          <FondCarte passes={passes} idPrefixe="sv2g" ech={ech} />
          {/* Vignette : un léger assombrissement des bords qui ramène l'œil
              vers la cible — même jeton de fond, donc juste dans les deux
              thèmes. */}
          <defs>
            <radialGradient id="sv2g-vignette" cx="50%" cy="46%" r="75%">
              <stop offset="58%" style={{ stopColor: 'var(--su-map-bg)', stopOpacity: 0 }} />
              <stop offset="100%" style={{ stopColor: 'var(--su-map-bg)', stopOpacity: 0.45 }} />
            </radialGradient>
          </defs>
          <rect x={vb.x} y={vb.y} width={vb.w} height={vb.h} fill="url(#sv2g-vignette)" pointerEvents="none" />
          {passes.map(p => <Halo key={p.n} passe={p} ech={ech} idPrefixe="sv2g" />)}
          {/* Balayage radar : uniquement tant que le moteur cherche. */}
          {enCours && pCible && (
            <g className="sv2c-sweep" style={{ transformOrigin: `${pCible.x}px ${pCible.y}px` }} pointerEvents="none">
              <path d={`M${pCible.x} ${pCible.y} L${pCible.x + 150 * ech} ${pCible.y} A${150 * ech} ${150 * ech} 0 0 0 ${(pCible.x + 150 * ech * 0.899).toFixed(1)} ${(pCible.y - 150 * ech * 0.438).toFixed(1)} Z`}
                fill="var(--su-coral)" opacity="0.09" />
              <line x1={pCible.x} y1={pCible.y} x2={pCible.x + 150 * ech} y2={pCible.y} stroke="var(--su-coral)" strokeWidth={1.2 * ech} opacity="0.45" />
            </g>
          )}
          {REPERES.map(([nom, lat, lng]) => {
            const p = proj(lat, lng);
            // Repère hors cadre : on ne le dessine pas dans la marge.
            if (p.x < vb.x + 8 * ech || p.x > vb.x + vb.w - 8 * ech || p.y < vb.y + 8 * ech || p.y > vb.y + vb.h - 8 * ech) return null;
            return (
              <g key={nom} pointerEvents="none">
                <circle cx={p.x} cy={p.y} r={4.5 * ech} fill="none" stroke="var(--su-map-ville)" strokeOpacity="0.4" strokeWidth={ech} />
                <circle cx={p.x} cy={p.y} r={2 * ech} fill="var(--su-map-ville)" />
                <text x={p.x + 7 * ech} y={p.y + 3 * ech} fontSize={10 * ech} fill="var(--su-map-ville)" fontFamily="'DM Sans','IBM Plex Sans',sans-serif">{nom}</text>
              </g>
            );
          })}
          <Cible centre={centre} ech={ech} enCours={enCours} />
          {enCours && pCible && (
            <circle className="sv2c-ping" cx={pCible.x} cy={pCible.y} r={11 * ech} fill="none" stroke="var(--su-coral)" strokeWidth={1.4 * ech} pointerEvents="none" />
          )}
          {groupes.map((g, i) => {
            const n = g.membres.length;
            const tete = g.membres[0];
            const actif = g.cle === choisie;
            // Un groupe grossit un peu avec son effectif, sans jamais devenir
            // une surface : c'est un compte, pas une mesure.
            const r = (n > 1 ? 7.5 : 5.5) * ech;
            const titre = n > 1
              ? `${n} profils · ${g.label || 'même position'}${g.approx ? ' · position approximative (chef-lieu)' : ''}`
              : `${nomDe(tete)}${tete.score != null ? ` · score ${Math.round(Number(tete.score))}` : ''} · passe ${tete.passe_origine ?? '—'}${g.label ? ` · ${g.label}` : ''}${g.approx ? ' · position approximative (chef-lieu)' : ''}`;
            return (
              <g key={g.cle} className="sv2c-clic" role="button" tabIndex={0}
                aria-label={`${titre}. Ouvrir ${n > 1 ? 'les fiches' : 'la fiche'}.`}
                onClick={() => ouvrir(g.cle)}
                onKeyDown={(e) => { if (e.key === 'Enter' || e.key === ' ') { e.preventDefault(); ouvrir(g.cle); } }}>
                <circle className="sv2c-focus" cx={g.p.x} cy={g.p.y} r={r + 6.5 * ech} fill="none" stroke="var(--su-map-ink)" strokeWidth={1.6 * ech} />
                {actif && <circle cx={g.p.x} cy={g.p.y} r={r + 5.5 * ech} fill="none" stroke="var(--su-coral)" strokeWidth={2 * ech} />}
                <circle className="sv2c-pt" style={{ animationDelay: `${Math.min(i, 14) * 70}ms` }}
                  cx={g.p.x} cy={g.p.y} r={r} fill={couleurPasse(tete.passe_origine ?? 1)}
                  fillOpacity={g.approx ? 0.5 : 1} stroke="var(--su-map-panel)" strokeWidth={1.8 * ech} strokeDasharray={g.approx ? `${2.5 * ech} ${2 * ech}` : undefined}>
                  <title>{titre}</title>
                </circle>
                {n > 1 && (
                  <text x={g.p.x} y={g.p.y + 3.2 * ech} textAnchor="middle" fontSize={8 * ech} fontWeight="700"
                    fill="var(--su-map-panel)" style={{ ...suData, pointerEvents: 'none' }}>{n}</text>
                )}
              </g>
            );
          })}
        </svg>

        <div className="sv2c-cam" role="group" aria-label="Caméra">
          <button type="button" className="sv2c-cam__btn" onClick={() => zoomer(1.6)} disabled={vb.w <= ZOOM_MIN + 0.5}
            aria-label="Zoomer" title="Zoomer (+)"><BI icon="plus-lg" /></button>
          <button type="button" className="sv2c-cam__btn" onClick={() => zoomer(1 / 1.6)} disabled={vb.w >= W - 0.5}
            aria-label="Dézoomer" title="Dézoomer (−)"><BI icon="dash-lg" /></button>
          {manuel && (
            <button type="button" className="sv2c-cam__btn sv2c-cam__btn--recadrer" onClick={recadrer}
              aria-label="Recadrer sur la campagne" title="Recadrer sur la campagne (0)"><BI icon="arrows-angle-contract" /></button>
          )}
        </div>

        {groupeOuvert && (
          <FicheProfil groupe={groupeOuvert} rang={Math.min(rang, groupeOuvert.membres.length - 1)}
            onRang={setRang} passes={passes} actions={actions} onFermer={() => setChoisie(null)} />
        )}

        {!legendeCachee && (
          <div style={{ position: 'absolute', left: '13px', bottom: '12px', background: 'var(--su-map-veil)', border: '1px solid var(--su-map-line)', borderRadius: '6px', padding: '10px 12px', minWidth: '190px', maxWidth: '58%' }}>
            <div style={{ ...suData, fontSize: '9px', letterSpacing: '0.12em', textTransform: 'uppercase', color: 'var(--su-map-ink)', fontWeight: 700, marginBottom: '7px' }}>Lecture de la cible</div>
            {passes.length === 0 && (
              <div style={{ fontSize: '10.5px', color: 'var(--su-map-sub)', margin: '4px 0' }}>
                {v2.statut === 'en_cours'
                  ? (centre ? 'Zone visée au lancement — la première passe n’a pas encore été publiée.' : 'Le moteur n’a pas encore annoncé de zone.')
                  : (centre ? 'Zone visée au lancement · recherche unique, sans élargissement.' : 'Les profils placés, sans zone annoncée par le moteur.')}
              </div>
            )}
            {passes.map(p => (
              <div key={p.n} style={{ display: 'flex', alignItems: 'center', gap: '7px', fontSize: '10.5px', color: 'var(--su-map-sub)', margin: '4px 0' }}>
                <span style={{ width: '8px', height: '8px', borderRadius: '50%', background: couleurPasse(p.n), flex: 'none' }} />
                Passe {p.n} · {p.zone?.label || NIVEAUX[p.zone?.niveau] || `zone ${p.n}`}
                {/* Trois vérités possibles : un rayon MESURÉ (emprise réelle du
                    lieu), un ordre de grandeur, ou un découpage sans rayon. */}
                {p.zone?.rayon_km != null && p.zone?.mesure === true && <span style={{ ...suData, fontSize: '9.5px', color: 'var(--su-map-label)' }}>{p.zone.rayon_km} km · mesuré</span>}
                {p.zone?.rayon_km != null && p.zone?.mesure !== true && <span style={{ ...suData, fontSize: '9.5px', color: 'var(--su-map-label)' }}>~{p.zone.rayon_km} km</span>}
                {p.zone?.rayon_km == null && <span style={{ color: 'var(--su-map-label)' }}>(indicative)</span>}
              </div>
            ))}
            {passes.some(p => p.zone?.rayon_km == null) && (
              <div style={{ display: 'flex', alignItems: 'center', gap: '7px', fontSize: '10.5px', color: 'var(--su-map-sub)', margin: '4px 0' }}>
                <span style={{ width: '17px', borderTop: '1px dashed var(--su-map-sub)', flex: 'none' }} />
                Surface indicative — découpage administratif, pas une mesure
              </div>
            )}
            {localises.length > 0 && (
              <div style={{ fontSize: '10.5px', color: 'var(--su-map-sub)', margin: '4px 0' }}>
                {localises.length} profil{localises.length > 1 ? 's' : ''} placé{localises.length > 1 ? 's' : ''}
                {groupes.length < localises.length ? ' — un point chiffré rassemble ceux que cette échelle superpose' : ''} — cliquez un point pour l’ouvrir
              </div>
            )}
            {(passes.length > 0 || localises.length > 0 || centre) && (
              <div style={{ fontSize: '10.5px', color: 'var(--su-map-label)', margin: '4px 0' }}>
                Molette ou boutons pour zoomer, glisser pour déplacer{manuel ? ' — la caméra reviendra au moteur à la prochaine passe' : ''}.
              </div>
            )}
            {nonLocalises > 0 && (
              <div title="Candidats sans coordonnées : aucune position n'est inventée, ils ne figurent pas sur la carte."
                style={{ display: 'flex', alignItems: 'center', gap: '7px', fontSize: '10.5px', color: 'var(--su-map-sub)', margin: '4px 0' }}>
                <span style={{ width: '8px', height: '8px', borderRadius: '50%', background: 'var(--su-map-label)', flex: 'none' }} />
                {nonLocalises} non localisé{nonLocalises > 1 ? 's' : ''} — compté, pas placé
              </div>
            )}
          </div>
        )}
        <button type="button" onClick={() => setLegendeCachee(v => !v)}
          style={{ position: 'absolute', right: '13px', bottom: '12px', height: '28px', padding: '0 11px', border: '1px solid var(--su-map-line)', background: 'var(--su-map-panel)', color: 'var(--su-map-sub)', borderRadius: '5px', fontFamily: "'DM Sans','IBM Plex Sans',sans-serif", fontSize: '10.5px', fontWeight: 600, cursor: 'pointer' }}>
          {legendeCachee ? 'Afficher la légende' : 'Masquer la légende'}
        </button>
      </div>
    </div>
  );
}

/* La fiche d'un profil choisi sur la carte. Les gestes sont EXACTEMENT ceux
   de la liste « Sélection reçue » (mêmes routes, mêmes contrôles) — et ils
   n'apparaissent que quand le serveur peut les tenir : tant que la sélection
   n'est pas arrivée, ajouter au besoin ou garder au vivier échouerait, donc
   on l'annonce plutôt que d'offrir le bouton. */
function FicheProfil({ groupe, rang, onRang, passes, actions, onFermer }) {
  const membres = groupe.membres;
  const candidat = membres[rang] || membres[0];
  const geo = lireGeo(candidat);
  const approx = estApprox(geo);
  const passe = passes.find(p => Number(p.n) === Number(candidat.passe_origine)) || null;
  const zone = passe?.zone?.label || (passe?.zone?.niveau ? (NIVEAUX[passe.zone.niveau] || passe.zone.niveau) : null);
  const couleur = couleurPasse(candidat.passe_origine ?? 1);
  const pretes = actions?.pretes === true;
  const occupe = actions?.occupeId != null && String(actions.occupeId) === String(candidat.recruiterId);
  // Le lien vers le profil chez SA source — Job-Room pour un candidat ORP,
  // GitHub, Recruiter… — et, pour l'ORP, le conseiller : c'est LUI le canal
  // de contact, pas un InMail. Tout vient du webhook ; seule exception, un
  // LinkedIn sans lien : l'URL Recruiter se déduit de l'identifiant (règle
  // du moteur), sinon les anciennes campagnes n'auraient jamais de lien.
  const src = String(candidat.source || (candidat.recruiterId && !String(candidat.recruiterId).includes(':') ? 'linkedin' : '')).toLowerCase();
  const lien = lienProfil(candidat, src);
  const estOrp = src === 'jobroom' || src === 'job-room' || String(candidat.recruiterId || '').startsWith('jr:');
  const libLien = estOrp ? 'Ouvrir sur Job-Room' : src === 'github' ? 'Voir sur GitHub' : src === 'linkedin' ? 'Ouvrir dans Recruiter' : 'Voir le profil';
  const icoLien = estOrp ? 'building' : src === 'github' ? 'github' : src === 'linkedin' ? 'linkedin' : 'box-arrow-up-right';
  const orp = candidat.orpAdvisor && (candidat.orpAdvisor.name || candidat.orpAdvisor.email || candidat.orpAdvisor.phone) ? candidat.orpAdvisor : null;
  const ajoute = actions?.ajoutes?.has?.(candidat.recruiterId);
  const garde = actions?.gardes?.has?.(candidat.recruiterId);
  const ligne = { display: 'flex', alignItems: 'baseline', gap: '7px', fontSize: '11px', color: 'var(--su-map-sub)', marginTop: '5px' };
  const tourne = (o) => (o ? { fontSize: '11px', display: 'inline-block', animation: 'sv2-spin .7s linear infinite' } : { fontSize: '11px' });
  return (
    <div role="dialog" aria-label={`Profil ${nomDe(candidat)}`}
      style={{ position: 'absolute', top: '12px', right: '12px', width: '242px', maxWidth: 'calc(100% - 24px)',
        // Une fiche ORP (lien + conseiller) est plus haute que la carte : elle
        // défile dans son cadre et s'arrête AU-DESSUS du bouton de légende.
        maxHeight: 'calc(100% - 64px)', overflowY: 'auto', boxSizing: 'border-box',
        background: 'var(--su-map-panel)', border: '1px solid var(--su-map-line)', borderRadius: '9px', padding: '12px 13px', fontFamily: "'DM Sans','IBM Plex Sans',sans-serif" }}>
      <button type="button" className="sv2f-fermer" onClick={onFermer} aria-label="Fermer la fiche">
        <BI icon="x-lg" style={{ fontSize: '11px' }} />
      </button>
      {membres.length > 1 && (
        <div style={{ marginBottom: '9px', paddingBottom: '9px', borderBottom: '1px solid var(--su-map-line)' }}>
          <div style={{ ...suData, fontSize: '9px', letterSpacing: '0.12em', textTransform: 'uppercase', color: 'var(--su-map-label)', paddingRight: '26px' }}>
            {membres.length} profils · {groupe.label || 'même position'}
          </div>
          <div style={{ display: 'flex', flexWrap: 'wrap', gap: '4px', marginTop: '6px' }}>
            {membres.map((m, i) => (
              <button key={cleDe(m, i)} type="button" onClick={() => onRang(i)}
                className={`sv2f-onglet${i === rang ? ' sv2f-onglet--on' : ''}`}
                aria-current={i === rang ? 'true' : undefined}>
                {nomDe(m)}
              </button>
            ))}
          </div>
        </div>
      )}
      <div style={{ display: 'flex', alignItems: 'flex-start', gap: '8px', paddingRight: membres.length > 1 ? 0 : '26px' }}>
        <span style={{ marginTop: '4px', width: '9px', height: '9px', borderRadius: '50%', background: couleur, flex: 'none' }} />
        <div style={{ flex: 1, minWidth: 0 }}>
          <div style={{ fontSize: '13px', fontWeight: 700, color: 'var(--su-map-ink)', lineHeight: 1.25 }}>{nomDe(candidat)}</div>
          {candidat.titre && <div style={{ fontSize: '11px', color: 'var(--su-map-sub)', marginTop: '2px' }}>{candidat.titre}</div>}
        </div>
        {candidat.score != null && (
          <span title="Score attribué par le moteur" style={{ ...suData, fontSize: '15px', fontWeight: 700, color: 'var(--warn)', flex: 'none' }}>{Math.round(Number(candidat.score))}</span>
        )}
      </div>

      <div style={{ marginTop: '9px', paddingTop: '9px', borderTop: '1px solid var(--su-map-line)' }}>
        <div style={ligne}>
          <BI icon="geo-alt" style={{ fontSize: '10px', flex: 'none' }} />
          <span style={{ color: 'var(--su-map-ink)' }}>{geo?.label || 'Localisation inconnue'}</span>
        </div>
        {approx && (
          <div style={{ ...ligne, marginTop: '3px', color: 'var(--su-map-label)' }}>
            <span style={{ fontSize: '10.5px' }}>Position ramenée au chef-lieu — approximative.</span>
          </div>
        )}
        <div style={ligne}>
          <BI icon="record-circle" style={{ fontSize: '10px', flex: 'none', color: couleur }} />
          <span>Passe {candidat.passe_origine ?? '—'}{zone ? ` · ${zone}` : ''}</span>
        </div>
        {candidat.elargi === true && (
          <div style={{ ...ligne, color: 'var(--warn)' }}>
            <BI icon="arrows-angle-expand" style={{ fontSize: '10px', flex: 'none' }} />
            <span>Trouvé en élargissement — hors de la zone initiale.</span>
          </div>
        )}
          {candidat?.ne_pas_contacter === true && <div style={{ marginTop: '7px' }}><BadgeNePasContacter candidat={candidat} /></div>}
{candidat && 'open_to_work' in candidat && <div style={{ marginTop: '7px' }}><BadgeOpenToWork candidat={candidat} /></div>}
        {candidat?.disponibilite_annoncee && <div style={{ marginTop: '7px' }}><BadgeDisponibilite candidat={candidat} /></div>}
        {candidat.clone === true && (
          <div style={{ marginTop: '7px' }}><BadgeClone candidat={candidat} nom={actions?.etalonNom} /></div>
        )}
        {Array.isArray(candidat.jackpot) && candidat.jackpot.length > 0 && (
          <div style={{ marginTop: '7px' }}><BadgeJackpot candidat={candidat} /></div>
        )}
        {Array.isArray(candidat.entreprise_visee) && candidat.entreprise_visee.length > 0 && (
          <div style={{ marginTop: '7px' }}><BadgeEntreprise candidat={candidat} /></div>
        )}
        {candidat.alerte_langue && Array.isArray(candidat.alerte_langue.manque) && candidat.alerte_langue.manque.length > 0 && (
          <div style={{ marginTop: '7px' }}><BadgeLangue candidat={candidat} /></div>
        )}
        {libelleCanal(candidat.source) && (
          <div style={ligne}><BI icon="diagram-3" style={{ fontSize: '10px', flex: 'none' }} /><span>{libelleCanal(candidat.source)}</span></div>
        )}
        {candidat.cv === true && (
          <div style={{ ...ligne, color: 'var(--ok)' }}><BI icon="file-earmark-check" style={{ fontSize: '10px', flex: 'none' }} /><span>CV reçu</span></div>
        )}
        {lien && (
          <a className="sv2f-btn sv2f-btn--lien" href={lien} target="_blank" rel="noreferrer" onClick={(e) => e.stopPropagation()}>
            <BI icon={icoLien} style={{ fontSize: '11px' }} /> {libLien}
          </a>
        )}
        {orp && (
          <div style={{ marginTop: '9px', paddingTop: '9px', borderTop: '1px solid var(--su-map-line)' }}>
            <div style={{ ...suData, fontSize: '9px', letterSpacing: '0.12em', textTransform: 'uppercase', color: 'var(--su-map-label)' }}>Conseiller ORP · le canal de contact</div>
            {orp.name && <div style={{ fontSize: '12px', fontWeight: 700, color: 'var(--su-map-ink)', marginTop: '4px' }}>{orp.name}</div>}
            {orp.office && <div style={{ fontSize: '11px', color: 'var(--su-map-sub)' }}>{orp.office}</div>}
            <div style={{ display: 'flex', gap: '6px', flexWrap: 'wrap', marginTop: '6px', alignItems: 'center' }}>
              {orp.email && <a className="sv2f-btn sv2f-btn--lien sv2f-btn--court" href={`mailto:${orp.email}`}><BI icon="envelope" style={{ fontSize: '11px' }} /> Écrire</a>}
              {orp.phone && <a className="sv2f-btn sv2f-btn--lien sv2f-btn--court" href={`tel:${String(orp.phone).replace(/\s+/g, '')}`}><BI icon="telephone" style={{ fontSize: '11px' }} /> {orp.phone}</a>}
              {candidat.orpAdvisorEmailed === true && <span style={{ fontSize: '10.5px', color: 'var(--ok)' }}><BI icon="check-lg" /> déjà prévenu par le moteur</span>}
            </div>
          </div>
        )}
      </div>

      <div style={{ marginTop: '10px', paddingTop: '10px', borderTop: '1px solid var(--su-map-line)', display: 'flex', flexDirection: 'column', gap: '6px' }}>
        {!pretes && (
          <div style={{ fontSize: '10.5px', lineHeight: 1.5, color: 'var(--su-map-label)' }}>
            La recherche tourne encore : les gestes s’ouvriront à la réception de la sélection.
          </div>
        )}
        {actions?.onClone && (
          <button type="button" className="sv2f-btn" onClick={() => actions.onClone(candidat)}
            title="Le moteur lit ce profil en entier et prépare la recherche de ses clones.">
            <BI icon="people" style={{ fontSize: '11px' }} /> Trouver son clone
          </button>
        )}
        {pretes && candidat.candidate_id != null && actions?.onOuvrirFiche && (
          <button type="button" className="sv2f-btn" onClick={() => actions.onOuvrirFiche(candidat.candidate_id)}>
            <BI icon="person-lines-fill" style={{ fontSize: '11px' }} /> Ouvrir la fiche candidat
          </button>
        )}
        {pretes && actions?.onAjouterAuBesoin && (
          ajoute || candidat.sur_besoin === true ? (
            <div className="sv2f-btn sv2f-fait">
              <BI icon="check-lg" style={{ fontSize: '11px' }} /> {ajoute ? 'Ajouté au besoin' : 'Déjà au besoin'}
            </div>
          ) : (
            <button type="button" className="sv2f-btn sv2f-btn--cta" disabled={occupe}
              onClick={() => actions.onAjouterAuBesoin(candidat)}>
              <BI icon={occupe ? 'arrow-repeat' : 'plus-lg'} style={tourne(occupe)} /> Ajouter au besoin
            </button>
          )
        )}
        {pretes && actions?.onGarderAuVivier && (
          garde ? (
            <div className="sv2f-btn sv2f-fait">
              <BI icon="check-lg" style={{ fontSize: '11px' }} /> Gardé au vivier
            </div>
          ) : (
            <button type="button" className="sv2f-btn" disabled={occupe} onClick={() => actions.onGarderAuVivier(candidat)}>
              <BI icon={occupe ? 'arrow-repeat' : 'bookmark-plus'} style={tourne(occupe)} /> Garder au vivier
            </button>
          )
        )}
      </div>
    </div>
  );
}

/** Badge JACKPOT : ce profil porte un mot rare guetté au lancement
 *  (« Regdata »). Le seul accent de l'écran, à dessein : c'est LA nouvelle. */
// V2.1 — le profil vient d'une entreprise visée (employeur actuel ou passé) :
// c'est là que le recruteur voulait piocher, il passe en tête.
// ALERTE LANGUE : le client attend une langue que ce profil ne déclare pas
// (section Langues de LinkedIn). Une alerte, jamais un refus — la section est
// souvent incomplète, on vérifie avant de présenter. Vécu 22.09 : « des
// candidats qui ne parlent qu'anglais, le client parle français ».
export function BadgeLangue({ candidat, style }) {
  const a = candidat?.alerte_langue;
  const manque = Array.isArray(a?.manque) ? a.manque.filter(Boolean) : [];
  if (!manque.length) return null;
  const parle = Array.isArray(a?.parle) ? a.parle.filter(Boolean) : [];
  return (
    <span title={`Le client attend ${manque.join(' et ')} ; ce profil déclare ${parle.length ? parle.join(', ') : 'aucune langue'}. À vérifier avant de présenter — la section Langues d'un profil est souvent incomplète.`}
      style={{ display: 'inline-flex', alignItems: 'center', gap: '5px', fontSize: '10.5px', fontWeight: 800, letterSpacing: '0.05em', textTransform: 'uppercase', padding: '2px 8px', borderRadius: '6px', background: 'var(--erp-warning-soft)', color: 'var(--erp-warning)', border: '1px solid var(--erp-warning)', ...(style || {}) }}>
      <BI icon="translate" style={{ fontSize: '9px' }} />
      ne déclare pas {manque.join(' ni ')}{parle.length ? ` · parle ${parle.join(', ')}` : ''}
    </span>
  );
}
export function BadgeEntreprise({ candidat, style }) {
  const noms = Array.isArray(candidat?.entreprise_visee) ? candidat.entreprise_visee.filter(Boolean) : [];
  if (!noms.length) return null;
  return (
    <span title={`Entreprise visée : ce profil travaille ou a travaillé chez ${noms.join(', ')} — l'une des sociétés où la campagne pioche.`}
      style={{ display: 'inline-flex', alignItems: 'center', gap: '5px', fontSize: '10.5px', fontWeight: 800, letterSpacing: '0.05em', textTransform: 'uppercase', padding: '2px 8px', borderRadius: '6px', border: '1px solid var(--erp-accent, var(--su-coral))', color: 'var(--erp-accent, var(--su-coral))', whiteSpace: 'nowrap', ...style }}>
      <BI icon="building" style={{ fontSize: '9px' }} />
      chez {noms.join(' · ')}
    </span>
  );
}
export function BadgeJackpot({ candidat, style }) {
  const mots = Array.isArray(candidat?.jackpot) ? candidat.jackpot.filter(Boolean) : [];
  if (!mots.length) return null;
  return (
    <span title={`Jackpot : ce profil porte ${mots.join(', ')} — le mot rare guetté au lancement de la campagne.`}
      style={{ display: 'inline-flex', alignItems: 'center', gap: '5px', fontSize: '10.5px', fontWeight: 800, letterSpacing: '0.05em', textTransform: 'uppercase', padding: '2px 8px', borderRadius: '6px', border: '1px solid var(--erp-accent, var(--su-coral))', color: 'var(--erp-accent, var(--su-coral))', whiteSpace: 'nowrap', ...style }}>
      <BI icon="key" style={{ fontSize: '9px' }} />
      Jackpot · {mots.join(' · ')}
    </span>
  );
}

/** Badge CLONE : le cerveau a jugé ce candidat capable de remplacer le profil
 *  étalon au pied levé — c'est la cible d'une campagne de clones. */
// « Ne pas contacter » : la fiche de ce profil est marquée — le serveur
// refusera l'InMail, l'appel et l'invitation ; on le montre AVANT le clic.
//
// `onLever` rend la marque RÉVERSIBLE là où elle s'affiche (vécu 09.09 :
// « je peux plus l'enlever de cette liste »). Sans lui, le badge reste ce
// qu'il était : un simple constat. Un écran qui ne connaît pas l'id de fiche
// du profil ne le passe pas — mieux vaut ne rien proposer qu'un bouton qui
// échoue.
//
// `compact` : le badge ne porte QUE le fait, le motif reste en infobulle.
// Vécu 26.09 : sur la ligne d'un candidat, « NE PAS CONTACTER · ARRÊTS
// MALADIE SUR ARRÊTS MALADIE - VIRÉ DE … » prenait toute la largeur — le nom
// disparaissait, et les boutons de la ligne sortaient de l'écran. Là où la
// place est comptée, le motif se lit SOUS LE NOM (l'écran appelant le pose),
// jamais dans la barre d'actions.
export function BadgeNePasContacter({ candidat, style, onLever, leverEnCours, compact = false }) {
  if (candidat?.ne_pas_contacter !== true) return null;
  const motif = candidat.ne_pas_contacter_motif ? String(candidat.ne_pas_contacter_motif) : '';
  const affiche = compact ? '' : motif;
  const badge = (
    <span title={`Ne pas contacter${motif ? ` — ${motif}` : ''} : la fiche est marquée, aucun InMail, appel ni invitation ne partira vers ce profil.`}
      style={{ display: 'inline-flex', alignItems: 'center', gap: '5px', fontSize: '10.5px', fontWeight: 800, letterSpacing: '0.05em', textTransform: 'uppercase', padding: '2px 8px', borderRadius: '6px', border: '1px solid var(--danger-fg)', color: 'var(--danger-fg)', whiteSpace: 'nowrap', maxWidth: '100%', overflow: 'hidden', textOverflow: 'ellipsis', ...(onLever ? {} : style) }}>
      <BI icon="slash-circle" style={{ fontSize: '9px' }} />
      Ne pas contacter{affiche ? ` · ${affiche}` : ''}
    </span>
  );
  if (!onLever) return badge;
  return (
    <span style={{ display: 'inline-flex', alignItems: 'center', gap: '6px', maxWidth: '100%', ...style }}>
      {badge}
      <button type="button" className="erp-btn erp-btn--secondary erp-btn--sm" disabled={leverEnCours}
        title="Lever la marque : les envois vers ce candidat redeviennent possibles"
        onClick={e => { e.stopPropagation(); onLever(); }}>
        <BI icon={leverEnCours ? 'arrow-repeat' : 'unlock'} style={leverEnCours ? { animation: 'ats-spin .7s linear infinite' } : undefined} /> Autoriser
      </button>
    </span>
  );
}
// « Open to work » : le signal qui change la priorité d'un contact. Trois
// états servis par le serveur (open_to_work) : vrai (le profil l'affiche),
// faux (vérifié, absent), null (pas encore vérifié) — on n'affiche jamais un
// « non » par défaut.
export function BadgeOpenToWork({ candidat, style }) {
  const v = candidat?.open_to_work;
  if (v !== true && v !== false && v !== null && v !== undefined) return null;
  const etat = v === true ? { l: 'Open to work', icone: 'briefcase-fill', fg: 'var(--ok)', titre: 'Ce profil affiche « open to work » : à contacter en premier.' }
    : v === false ? { l: 'Pas en recherche affichée', icone: 'briefcase', fg: 'var(--erp-text-tertiary)', titre: 'Profil vérifié : il n’affiche pas « open to work ».' }
      : { l: 'Open to work : non vérifié', icone: 'question-circle', fg: 'var(--erp-text-tertiary)', titre: 'Le statut « open to work » n’a pas encore été lu sur ce profil — « Vérifier open to work » sur la campagne.' };
  return (
    <span title={etat.titre}
      style={{ display: 'inline-flex', alignItems: 'center', gap: '5px', fontSize: '10.5px', fontWeight: v === true ? 800 : 600, letterSpacing: v === true ? '0.05em' : 0, textTransform: v === true ? 'uppercase' : 'none',
        padding: '2px 8px', borderRadius: '6px', border: `1px solid ${v === true ? 'var(--ok)' : 'var(--hair)'}`, color: etat.fg, whiteSpace: 'nowrap', ...style }}>
      <BI icon={etat.icone} style={{ fontSize: '9px' }} />
      {etat.l}
    </span>
  );
}
/** Ce que le candidat a dit de sa disponibilité (approche directe) : quand
    il pourrait commencer et son préavis — la phrase vient du serveur
    (`disponibilite_annoncee.libelle`), jamais recomposée ici. */
export function BadgeDisponibilite({ candidat, style }) {
  const d = candidat?.disponibilite_annoncee;
  if (!d || typeof d !== 'object') return null;
  const texte = d.libelle || [d.texte, d.preavis ? `préavis ${d.preavis}` : null].filter(Boolean).join(' · ');
  if (!texte) return null;
  const quand = d.le ? new Date(d.le).toLocaleDateString('fr-CH', { day: '2-digit', month: '2-digit' }) : null;
  return (
    <span title={`Dit par le candidat dans sa réponse${quand ? ` le ${quand}` : ''} : quand il pourrait commencer${d.preavis ? ' et son préavis' : ''}. Écrit sur sa fiche si sa case était vide.`}
      style={{ display: 'inline-flex', alignItems: 'center', gap: '5px', fontSize: '10.5px', fontWeight: 600,
        padding: '2px 8px', borderRadius: '6px', border: '1px solid var(--ok)', color: 'var(--ok)', whiteSpace: 'nowrap', ...style }}>
      <BI icon="calendar-check-fill" style={{ fontSize: '9px' }} />
      {texte}
    </span>
  );
}
/** « Sans Quarkus » : une techno nommée dans l'intitulé du poste et absente
    du profil — le moteur plafonne le score, le recruteur le voit avant de
    cliquer. `manque` vient du scoring du moteur, jamais déduit ici. */
export function BadgeManque({ candidat, style }) {
  const l = Array.isArray(candidat?.manque) ? candidat.manque.map(x => String(x || '').trim()).filter(Boolean) : [];
  if (!l.length) return null;
  return (
    <span title={`Le profil ne montre pas ${l.join(', ')} — nommé${l.length > 1 ? 's' : ''} dans l’intitulé du poste : le moteur plafonne le score. À vérifier avant de contacter.`}
      style={{ display: 'inline-flex', alignItems: 'center', gap: '5px', fontSize: '10.5px', fontWeight: 800, letterSpacing: '0.05em', textTransform: 'uppercase', padding: '2px 8px', borderRadius: '6px', border: '1px solid var(--erp-warning)', background: 'var(--erp-warning-soft)', color: 'var(--erp-warning)', whiteSpace: 'nowrap', ...style }}>
      <BI icon="exclamation-triangle-fill" style={{ fontSize: '9px' }} />
      Sans {l.join(', ')}
    </span>
  );
}
/** « À compléter » (V2.2) : un profil LinkedIn sous le seuil parce qu'il ne
    dit rien (titre générique, aucune mission décrite, résumé vide) mais qui
    porte un signal — à l'écoute d'opportunités, dans la zone, chez une
    entreprise visée, un mot rare. Le moteur ne l'écarte pas : on lui demande
    son CV pour le noter. `raison_incomplet` et `signaux` viennent du moteur. */
export function BadgeACompleter({ candidat, style }) {
  if (candidat?.a_completer !== true) return null;
  const raison = String(candidat.raison_incomplet || '').trim();
  const signaux = Array.isArray(candidat.signaux) ? candidat.signaux.map(x => String(x || '').trim()).filter(Boolean) : [];
  return (
    <span title={`Profil peu renseigné${raison ? ` (${raison})` : ''}${signaux.length ? ` mais porteur d’un signal : ${signaux.join(', ')}` : ''}. Le moteur ne l’a pas écarté : demandez son CV pour le noter.`}
      style={{ display: 'inline-flex', alignItems: 'center', gap: '5px', fontSize: '10.5px', fontWeight: 800, letterSpacing: '0.05em', textTransform: 'uppercase', padding: '2px 8px', borderRadius: '6px', border: '1px solid var(--erp-accent)', color: 'var(--erp-accent)', whiteSpace: 'nowrap', ...style }}>
      <BI icon="person-lines-fill" style={{ fontSize: '9px' }} />
      À compléter
    </span>
  );
}
export function BadgeClone({ candidat, nom, style }) {
  if (candidat?.clone !== true) return null;
  return (
    <span title={`Clone${nom ? ` de ${nom}` : ''} : ce profil remplit les critères du clone — c'est la cible de la campagne.`}
      style={{ display: 'inline-flex', alignItems: 'center', gap: '5px', fontSize: '10.5px', fontWeight: 800, letterSpacing: '0.05em', textTransform: 'uppercase', padding: '2px 8px', borderRadius: '6px', background: 'var(--erp-accent, var(--su-coral))', color: 'var(--accent-fg, var(--surface))', whiteSpace: 'nowrap', ...style }}>
      <BI icon="people-fill" style={{ fontSize: '9px' }} />
      Clone{nom ? ` de ${nom}` : ''}
    </span>
  );
}

/** Badge « trouvé en élargissement » d'une carte candidat de la sélection. */
export function BadgeElargi({ candidat, passes }) {
  if (candidat?.elargi !== true) return null;
  const passe = Array.isArray(passes) ? passes.find(p => Number(p.n) === Number(candidat.passe_origine)) : null;
  const zone = passe?.zone?.label || (passe?.zone?.niveau ? (NIVEAUX[passe.zone.niveau] || passe.zone.niveau) : null);
  return (
    <span title="Ce profil n'est pas sorti de la zone initiale : le moteur l'a trouvé en élargissant la recherche."
      style={{ display: 'inline-flex', alignItems: 'center', gap: '5px', fontSize: '10.5px', fontWeight: 600, padding: '2px 8px', borderRadius: '6px', background: 'rgba(217,119,6,0.13)', color: 'var(--warn)', whiteSpace: 'nowrap' }}>
      <BI icon="arrows-angle-expand" style={{ fontSize: '9px' }} />
      trouvé en élargissement{candidat.passe_origine != null ? ` — passe ${candidat.passe_origine}` : ''}{zone ? ` (${zone})` : ''}
    </span>
  );
}
